import type { PlaygroundProjectFile } from "../auth/api-client";

const SHADOW_PREFIX = "ccs.playground.shadow.";

export function playgroundShadowKey(projectId: string): string {
  return `${SHADOW_PREFIX}${projectId}`;
}

export function playgroundFilesEqual(
  a: readonly PlaygroundProjectFile[],
  b: readonly PlaygroundProjectFile[]
): boolean {
  const canonical = (files: readonly PlaygroundProjectFile[]) =>
    JSON.stringify(
      [...files]
        .map((file) => ({ path: file.path, content: file.content }))
        .sort((left, right) => left.path.localeCompare(right.path))
    );
  return canonical(a) === canonical(b);
}

export function readPlaygroundShadow(projectId: string): PlaygroundProjectFile[] | null {
  try {
    const raw = localStorage.getItem(playgroundShadowKey(projectId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    const files: PlaygroundProjectFile[] = [];
    for (const item of parsed) {
      if (!item || typeof item !== "object") continue;
      const record = item as Record<string, unknown>;
      if (typeof record.path === "string" && typeof record.content === "string") {
        files.push({ path: record.path, content: record.content });
      }
    }
    return files.length > 0 ? files : null;
  } catch {
    return null;
  }
}

export function writePlaygroundShadow(projectId: string, files: readonly PlaygroundProjectFile[]): void {
  try {
    localStorage.setItem(
      playgroundShadowKey(projectId),
      JSON.stringify(files.map((file) => ({ path: file.path, content: file.content })))
    );
  } catch {
    /* quota or private mode: the in-memory editor still has the code */
  }
}

export function clearPlaygroundShadow(projectId: string): void {
  try {
    localStorage.removeItem(playgroundShadowKey(projectId));
  } catch {
    /* ignore */
  }
}

/** Prefer this browser's unsynced copy over the last account save. */
export function filesPreferringShadow(
  projectId: string,
  serverFiles: readonly PlaygroundProjectFile[]
): { files: PlaygroundProjectFile[]; recovered: boolean } {
  const shadow = readPlaygroundShadow(projectId);
  if (!shadow || playgroundFilesEqual(shadow, serverFiles)) {
    if (shadow) clearPlaygroundShadow(projectId);
    return { files: [...serverFiles], recovered: false };
  }
  return { files: shadow, recovered: true };
}
